import './index.css'
import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import App from './App'

import { initPostHog, posthog } from './analytics/posthog'
import { getPlayerId } from './analytics/playerId'
import { initSentry } from './monitoring/sentry'

// Initialize Sentry as early as possible
initSentry()

function Bootstrap() {
  useEffect(() => {
    const enabled = initPostHog()
    if (!enabled) return

    const sessionStart = typeof performance !== 'undefined' ? performance.now() : Date.now()
    let sessionEnded = false
    const emitSessionEnd = (reason: string) => {
      if (sessionEnded) return
      sessionEnded = true
      const endedAt = typeof performance !== 'undefined' ? performance.now() : Date.now()
      const durationMs = Math.max(0, endedAt - sessionStart)
      import('./analytics/track')
        .then(({ track }) => track('session_end', { duration_ms: durationMs, reason }))
        .catch((err) => console.warn('[analytics] track load failed', err))
    }

    try {
      const playerId = getPlayerId()
      posthog.identify(playerId)
    } catch (err) {
      console.warn('[analytics] identify failed', err)
    }

    import('./analytics/track')
      .then(({ track }) => {
        track('session_start')
        track('app_open')
      })
      .catch((err) => console.warn('[analytics] track load failed', err))

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') emitSessionEnd('hidden')
    }
    const handleBeforeUnload = () => emitSessionEnd('unload')

    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('pagehide', handleBeforeUnload)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      emitSessionEnd('cleanup')
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('pagehide', handleBeforeUnload)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  return (
    <StrictMode>
      <App />
    </StrictMode>
  )
}

createRoot(document.getElementById('root')!).render(
  <Bootstrap />
)
